import AppComponent from '../base/app-component.ts';
import { apiGet } from '../lib/api-client.ts';
import { templateParseToElement } from '../lib/template-parse.ts';

interface LavoratoreLite {
  id: number;
  cognome: string | null;
  nome: string | null;
  codiceFiscale: string | null;
  dataNascita?: string | null;
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 20;

/**
 * wc-lavoratore-picker - Autocomplete lavoratore per form (deleghe,
 * iscrizioni non-EDC, ...). Parallelo a wc-impresa-picker.
 *
 * Attributi:
 *   placeholder (string)
 *
 * Eventi:
 *   lavoratore-selected   detail: LavoratoreLite
 *   lavoratore-cleared    detail: {}
 */
export class LavoratorePicker extends AppComponent {
  static override componentName = 'wc-lavoratore-picker';
  protected override get templateId() { return 'lavoratore-picker'; }

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastQuery = '';
  private selected: LavoratoreLite | null = null;
  private cache = new Map<number, LavoratoreLite>();

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      placeholder: this.attributeString('placeholder', 'Cerca lavoratore (cognome, nome o CF)'),
    };
  }

  protected override afterRender(): void {
    this.delegate.on<InputEvent>('input', '[data-picker-input]', (e) => {
      const value = (e.target as HTMLInputElement).value.trim();
      this.scheduleSearch(value);
    });

    this.delegate.on<MouseEvent>('click', '[data-action="picker-clear"]', () => {
      this.clearSelection();
    });

    this.delegate.on<MouseEvent>('click', '[data-action="picker-select"]', (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-lavoratore-id]');
      if (!row) return;
      const id = Number(row.dataset.lavoratoreId);
      const lav = this.cache.get(id);
      if (lav) this.selectLavoratore(lav);
    });
  }

  protected override onDisconnected(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  getSelected(): LavoratoreLite | null {
    return this.selected;
  }

  setSelected(lav: LavoratoreLite | null): void {
    if (lav) this.selectLavoratore(lav);
    else this.clearSelection();
  }

  private scheduleSearch(value: string): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (value.length < MIN_CHARS) {
      this.lastQuery = '';
      this.renderResults([]);
      return;
    }
    if (value === this.lastQuery) return;
    this.debounceTimer = setTimeout(() => this.executeSearch(value), DEBOUNCE_MS);
  }

  private async executeSearch(q: string): Promise<void> {
    this.lastQuery = q;
    const container = this.$('[data-picker-results]');
    if (!container) return;

    container.classList.remove('d-none');
    templateParseToElement('lavoratore-picker-loading', container, {});

    const result = await apiGet<{ risultati: LavoratoreLite[]; count: number }>(
      `lavoratori?q=${encodeURIComponent(q)}&limit=${MAX_RESULTS}`,
    );
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    for (const r of result.data.risultati) this.cache.set(r.id, r);
    this.renderResults(result.data.risultati);
  }

  private renderResults(risultati: LavoratoreLite[]): void {
    const container = this.$('[data-picker-results]');
    if (!container) return;
    if (risultati.length === 0 && this.lastQuery === '') {
      container.classList.add('d-none');
      container.innerHTML = '';
      return;
    }
    container.classList.remove('d-none');
    templateParseToElement('lavoratore-picker-results', container, {
      risultati,
      empty: risultati.length === 0,
    });
  }

  private selectLavoratore(lav: LavoratoreLite): void {
    this.selected = lav;
    const input = this.$('[data-picker-input]') as HTMLInputElement | null;
    const display = this.$('[data-picker-selected]');
    const container = this.$('[data-picker-results]');
    if (input) input.value = '';
    if (container) { container.classList.add('d-none'); container.innerHTML = ''; }
    if (display) {
      display.classList.remove('d-none');
      templateParseToElement('lavoratore-picker-selected', display, { lavoratore: lav });
    }
    this.dispatch('lavoratore-selected', lav);
  }

  private clearSelection(): void {
    this.selected = null;
    const display = this.$('[data-picker-selected]');
    if (display) { display.classList.add('d-none'); display.innerHTML = ''; }
    this.dispatch('lavoratore-cleared');
  }
}
